/**
 * localStorage工具函数
 * 处理首次访问时的数据存储逻辑
 */

/**
 * 获取URL参数
 * @param {string} paramName - 参数名称
 * @returns {string|null} - 参数值或null
 */
export function getUrlParameter(paramName) {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(paramName);
}

/**
 * 检查并设置task_timestamp
 * 如果localStorage中没有task_timestamp，则设置为当前时间戳
 */
export function initTaskTimestamp() {
    const taskTimestamp = localStorage.getItem("task_timestamp");
    if (!taskTimestamp) {
        const currentTimestamp = Date.now().toString();
        localStorage.setItem("task_timestamp", currentTimestamp);
        console.log("Task timestamp initialized:", currentTimestamp);
    } else {
        console.log("Task timestamp already exists:", taskTimestamp);
    }
}

/**
 * 强制更新task_timestamp
 * 无论是否已存在，都重新设置为当前时间戳
 */
export function forceUpdateTaskTimestamp() {
    const currentTimestamp = Date.now().toString();
    localStorage.setItem("task_timestamp", currentTimestamp);
    console.log("Task timestamp force updated:", currentTimestamp);
}

/**
 * 检查并设置click_id
 * 从URL参数中获取aff_sub1，如果存在则存储到localStorage的click_id中
 * 如果没有aff_sub1参数，则使用当前时间戳作为click_id
 */
export function updateClickId() {
    const affSub1 = getUrlParameter("aff_sub1");
    if (affSub1) {
        localStorage.setItem("click_id", affSub1);
        console.log("Click ID updated from aff_sub1:", affSub1);
    } else {
        // 如果没有aff_sub1参数，检查是否已有click_id
        const existingClickId = localStorage.getItem("click_id");
        if (!existingClickId) {
            // 如果没有现有的click_id，使用当前时间戳
            const currentTimestamp = Date.now().toString();
            localStorage.setItem("click_id", currentTimestamp);
            console.log("Click ID set to current timestamp:", currentTimestamp);
        } else {
            console.log("Existing click_id found, keeping it:", existingClickId);
        }
    }
}

/**
 * 强制更新click_id
 * 重新按照规则设置click_id：优先使用aff_sub1，否则使用当前时间戳
 */
export function forceUpdateClickId() {
    const affSub1 = getUrlParameter("aff_sub1");
    if (affSub1) {
        localStorage.setItem("click_id", affSub1);
        console.log("Click ID force updated from aff_sub1:", affSub1);
    } else {
        // 如果没有aff_sub1参数，使用当前时间戳
        const currentTimestamp = Date.now().toString();
        localStorage.setItem("click_id", currentTimestamp);
        console.log("Click ID force updated to current timestamp:", currentTimestamp);
    }
}

/**
 * 检查并设置offer_id
 * 从URL参数中获取offer_id，如果存在则存储到localStorage中
 */
export function updateOfferId() {
    const offerId = getUrlParameter("offer_id");
    if (offerId) {
        localStorage.setItem("offer_id", offerId);
        console.log("Offer ID updated:", offerId);
    } else {
        console.log("No offer_id parameter found in URL");
    }
}

/**
 * 初始化首次访问逻辑
 * 执行所有必要的localStorage设置
 */
export function initFirstVisit() {
    console.log("Initializing first visit logic...");

    // 设置task_timestamp（仅在首次访问时）
    initTaskTimestamp();

    // 检查并更新click_id（每次有新值时覆盖）
    updateClickId();

    // 检查并更新offer_id（每次有新值时覆盖）
    updateOfferId();

    console.log("First visit initialization completed");
}

/**
 * 首页访问时的强制更新逻辑
 * 每次访问首页都重新设置click_id和task_timestamp
 */
export function initHomepageVisit() {
    console.log("Initializing homepage visit logic...");

    // 强制更新task_timestamp
    forceUpdateTaskTimestamp();

    // 强制更新click_id
    forceUpdateClickId();

    // 检查并更新offer_id（每次有新值时覆盖）
    updateOfferId();

    console.log("Homepage visit initialization completed");
}

/**
 * 获取localStorage中的值
 * @param {string} key - 键名
 * @returns {string|null} - 值或null
 */
export function getStorageValue(key) {
    return localStorage.getItem(key);
}

/**
 * 打印当前localStorage中的相关值（用于调试）
 */
export function debugStorageValues() {
    console.log("Current localStorage values:");
    console.log("task_timestamp:", getStorageValue("task_timestamp"));
    console.log("click_id:", getStorageValue("click_id"));
    console.log("offer_id:", getStorageValue("offer_id"));
}

/**
 * 日志记录函数
 * @param {string} event_type - 事件类型，格式为 "页面名_状态"（如 "year_load", "name_submit"）
 */
export function leadsgetLogger(event_type) {
    const task_timestamp = window.localStorage.getItem("task_timestamp");
    const offer_id = window.localStorage.getItem("offer_id");
    const click_id = window.localStorage.getItem("click_id");
    const _event_data = window.localStorage.getItem("quoteData");

    // 构建请求URL
    const url =
        "https://leadsget.top/api/event_track/?" +
        "&offer_id=" +
        (offer_id || "") +
        "&task_timestamp=" +
        (task_timestamp || "") +
        "&click_id=" +
        (click_id || "") +
        "&event_type=" +
        event_type +
        "&host_name=" +
        "policyautoquotes.com/" +
        "&event_data=" +
        encodeURIComponent(_event_data || "");

    // 发送请求
    const track_xhr = new XMLHttpRequest();
    track_xhr.open("GET", url);
    track_xhr.send();

    // 调试日志
    console.log("Logging event:", event_type, {
        task_timestamp,
        offer_id,
        click_id,
        event_data: _event_data,
    });
}

/**
 * 获取当前路由名称（从路径中提取）
 * @returns {string} 路由名称
 */
export function getCurrentRouteName() {
    const path = window.location.pathname;
    const routeName = path.split("/").pop() || "home";
    return routeName.toLowerCase();
}

/**
 * 记录页面加载事件
 * @param {string} routeName - 路由名称（可选，不传则自动获取）
 */
export function logPageLoad(routeName = null) {
    console.log("Logging page load:", routeName);
    //   const pageName = routeName || getCurrentRouteName();
    //   const eventType = `${pageName}_load`;
    //   leadsgetLogger(eventType);
}

/**
 * 提交用户数据到API
 * @param {boolean} isComplete - 是否为最终提交（complete=1）
 * @returns {Promise<boolean>} - 提交是否成功
 */
export async function submitUserData(isComplete = false) {
    try {
        // 获取用户数据
        const quoteData = localStorage.getItem("quoteData");
        if (!quoteData) {
            console.log("No quote data found, skipping submission");
            return false;
        }

        // 获取click_id
        const clickId = localStorage.getItem("click_id") || "";

        // 构建API URL
        let apiUrl = clickId ? `https://leadsget.top/api/car_insurances/?click_id=${encodeURIComponent(clickId)}` : "https://leadsget.top/api/car_insurances/";

        // 如果是最终提交，添加complete=1参数
        if (isComplete) {
            const separator = clickId ? "&" : "?";
            apiUrl += `${separator}complete=1`;
        }

        // 解析数据
        const submitData = JSON.parse(quoteData);

        console.log("Submitting data to API:", apiUrl);
        console.log("Data:", submitData);

        // 发送POST请求
        const response = await fetch(apiUrl, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(submitData),
        });

        if (response.ok) {
            const result = await response.json();
            console.log("API submission successful:", result);
            return true;
        } else {
            console.error("API submission failed:", response.status, response.statusText);
            return false;
        }
    } catch (error) {
        console.error("Error submitting user data:", error);
        return false;
    }
}

/**
 * 记录页面提交/跳转事件
 * @param {string} routeName - 路由名称（可选，不传则自动获取）
 */
export function logPageSubmit(routeName = null) {
    const pageName = routeName || getCurrentRouteName();
    const eventType = `${pageName}_submit`;
    leadsgetLogger(eventType);
}

/**
 * 提交数据并记录页面提交事件的组合函数
 * @param {string} routeName - 路由名称（可选，不传则自动获取）
 * @param {boolean} isComplete - 是否为最终提交（complete=1）
 * @param {boolean} waitForCompletion - 是否等待请求完成（默认false，GetQuotes页面为true）
 */
export async function submitDataAndLog(routeName = null, isComplete = false, waitForCompletion = false) {
    if (waitForCompletion) {
        // GetQuotes页面：等待请求完成
        if (isComplete) {
            await submitUserData(isComplete);
        }
        logPageSubmit(routeName);
    } else {
        // 其他页面：不等待请求完成，直接发送请求并记录日志
        if (isComplete) {
            submitUserData(isComplete).catch((error) => {
                console.error("Background data submission failed:", error);
            });
        }
        logPageSubmit(routeName);
    }
}
